import { Check, CheckCircle2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@ooh/ui/components/button';
import { Card, CardContent, CardHeader, CardTitle } from '@ooh/ui/components/card';
import { formatDisplayDateRange } from '@ooh/ui/lib/date-format';
import { BRAND_MARK } from '../../lib/brand';
import { ClientOfferPageHeader } from '../client-offer-review/client-offer-page-header';
import { PublicOfferSuccessSteps } from './public-offer-success-steps';

interface PublicOfferSuccessPageProps {
  clientName: string;
  offerTitle: string;
  campaignFrom: Date | null;
  campaignTo: Date | null;
  /** The way back into the offer (client-offer-interactive-view / Post-Acceptance
   * Read-Only Client View) — renders the read-only view with the recorded decisions,
   * without a full page reload. */
  onViewOffer: () => void;
}

export function PublicOfferSuccessPage({
  clientName,
  offerTitle,
  campaignFrom,
  campaignTo,
  onViewOffer,
}: PublicOfferSuccessPageProps) {
  const { t } = useTranslation('offers');
  const campaignRange =
    campaignFrom && campaignTo ? formatDisplayDateRange(campaignFrom, campaignTo) : null;
  const offerMeta = campaignRange ? `${clientName} · ${campaignRange}` : clientName;
  const steps = [
    {
      title: t('publicOffer.success.steps.saved.title'),
      description: t('publicOffer.success.steps.saved.description'),
    },
    {
      title: t('publicOffer.success.steps.availability.title'),
      description: t('publicOffer.success.steps.availability.description'),
    },
    {
      title: t('publicOffer.success.steps.contact.title'),
      description: t('publicOffer.success.steps.contact.description'),
    },
  ];

  return (
    <div
      className="flex min-h-screen flex-col bg-background"
      data-testid="public-offer-success-page"
    >
      <ClientOfferPageHeader
        brandInitial={BRAND_MARK}
        brandName={t('publicOffer.success.brandName')}
        brandSubtitle={t('publicOffer.success.brandSubtitle')}
        offerTitle={offerTitle}
        offerMeta={offerMeta}
        statusVariant="success"
        statusLabel={t('clientOffer.status.accepted')}
      />

      <main className="flex flex-1 items-start justify-center bg-[radial-gradient(circle_at_50%_0%,color-mix(in_hsl,var(--status-free)_7%,transparent),transparent_34%)] px-4 py-10 sm:items-center sm:px-6 sm:py-14">
        <section
          className="w-full max-w-2xl text-center"
          aria-labelledby="public-offer-success-title"
        >
          <div className="mx-auto mb-5 grid size-16 place-items-center rounded-full bg-status-free/15 text-status-free sm:mb-6 sm:size-[4.5rem]">
            <CheckCircle2 className="size-8 sm:size-9" strokeWidth={1.8} aria-hidden="true" />
          </div>

          <h1
            id="public-offer-success-title"
            className="text-3xl font-semibold leading-tight tracking-tight text-foreground sm:text-4xl"
          >
            {t('publicOffer.success.title')}
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-sm leading-7 text-muted-foreground sm:text-base">
            {t('publicOffer.success.description')}
          </p>
          <p className="mt-6 inline-flex items-center gap-2 text-xs font-semibold text-status-free">
            <Check className="size-4" strokeWidth={2} aria-hidden="true" />
            {t('publicOffer.success.savedLabel')}
          </p>

          <Card className="mt-8 text-left shadow-none sm:mt-10">
            <CardHeader className="border-b border-border/60 px-5 py-4 sm:px-6 sm:py-5">
              <CardTitle className="text-xs font-semibold">
                {t('publicOffer.success.nextTitle')}
              </CardTitle>
            </CardHeader>
            <CardContent className="px-5 py-5 sm:px-6 sm:py-6">
              <PublicOfferSuccessSteps steps={steps} />
            </CardContent>
          </Card>

          <p className="mt-5 text-2xs text-muted-foreground sm:mt-6">
            {t('publicOffer.success.closeHint')}
          </p>
          <Button
            type="button"
            variant="outline"
            onClick={onViewOffer}
            data-testid="public-offer-view-decisions"
            className="mt-4"
          >
            {t('publicOffer.success.backToOfferAction')}
          </Button>
        </section>
      </main>
    </div>
  );
}
